import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import {
  isOrganisationAuthorityCommandsFeatureEnabled,
  isOrgContextFeatureEnabled,
  isOrgPermissionReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { withUiDeadline } from "@/utils/uiDeadline";

const CONTEXT_PAGE_SIZE = 50;
const ADMINISTRATION_PAGE_SIZE = 10;
const MAX_RECENT_ORGANISATIONS = 5;
const CANONICAL_ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const LEGACY_ORG_ID_PATTERN = /^[A-Za-z0-9._:@-]{1,128}$/;
const ASSET_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const PUBLIC_IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;
const CURRENCY_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._-]{0,15}$/;
const CURSOR_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const ADMINISTRATION_CURSOR_PATTERN =
  /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const OPAQUE_REFERENCE_PATTERN = /^[a-f0-9]{64}$/;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;
const CONTEXT_KEYS = Object.freeze([
  "orgId",
  "name",
  "avatarAssetId",
  "currency",
  "membershipStatus",
]);
const OWN_CONTEXT_KEYS = Object.freeze([
  "schemaVersion",
  "orgId",
  "organisation",
  "membershipStatus",
  "membershipRevision",
  "permissions",
]);
const ORGANISATION_KEYS = Object.freeze([
  "publicIdentifier",
  "name",
  "description",
  "category",
  "avatarAssetId",
  "visibility",
  "economy",
  "currency",
  "joinPolicy",
  "revision",
]);
const PERMISSION_KEYS = Object.freeze([
  "canManageOrganisation",
  "canManageMembers",
  "canManageRoles",
  "canManageAppearance",
  "canModerateItems",
  "canManageWallet",
  "canManageMonetaryPolicy",
  "canManageSiblingRelationships",
  "canManageApiKeys",
]);
const ROLE_CAPABILITY_KEYS = Object.freeze([
  "organisationAdministration",
  "membershipAdministration",
  "roleAdministration",
  "appearanceAdministration",
  "moderateItemPlacements",
  "walletAdministration",
  "monetaryAdministration",
  "siblingRelationshipAdministration",
  "apiKeyAdministration",
]);

export class OrganisationContextServiceError extends Error {
  constructor(code = "unknown") {
    super("Organisation contexts could not be loaded.");
    this.name = "OrganisationContextServiceError";
    this.code = code;
  }
}

function fail(code) {
  throw new OrganisationContextServiceError(code);
}

function captureActor(authInstance) {
  const actor = authInstance?.currentUser;
  if (
    !actor ||
    typeof actor.uid !== "string" ||
    actor.uid.length < 1 ||
    actor.uid.length > 128
  ) {
    fail("unauthenticated");
  }
  return actor;
}

function assertActorIsCurrent(authInstance, actor) {
  if (
    authInstance?.currentUser !== actor ||
    authInstance.currentUser?.uid !== actor.uid
  ) {
    fail("account-changed");
  }
}

function hasExactKeys(value, keys) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return false;
  const actualKeys = Object.keys(value).sort();
  const expectedKeys = [...keys].sort();
  return (
    actualKeys.length === expectedKeys.length &&
    actualKeys.every((key, index) => key === expectedKeys[index])
  );
}

function isCanonicalOrgId(value) {
  return typeof value === "string" && CANONICAL_ORG_ID_PATTERN.test(value);
}

function isLegacyOrgId(value) {
  return (
    typeof value === "string" &&
    LEGACY_ORG_ID_PATTERN.test(value) &&
    value !== "." &&
    value !== ".."
  );
}

function isBoundedText(value, { minimum = 0, maximum, nullable = false }) {
  if (nullable && value === null) return true;
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length >= minimum &&
    value.length <= maximum &&
    !CONTROL_CHARACTERS.test(value)
  );
}

function normalizeListOptions(value) {
  const options = value === undefined ? {} : value;
  const prototype =
    options && typeof options === "object"
      ? Object.getPrototypeOf(options)
      : undefined;
  if (
    !options ||
    typeof options !== "object" ||
    Array.isArray(options) ||
    (prototype !== Object.prototype && prototype !== null) ||
    Object.keys(options).some(
      (key) => !["cursor", "search", "recentOrgUIDs"].includes(key),
    )
  ) {
    fail("invalid-argument");
  }
  const cursor = options.cursor ?? null;
  const searchValue = options.search ?? "";
  const recentOrgUIDs = options.recentOrgUIDs ?? [];
  if (
    (cursor !== null &&
      (typeof cursor !== "string" ||
        cursor.length < 64 ||
        cursor.length > 1024 ||
        !CURSOR_PATTERN.test(cursor))) ||
    typeof searchValue !== "string" ||
    searchValue.length > 80 ||
    CONTROL_CHARACTERS.test(searchValue) ||
    !Array.isArray(recentOrgUIDs) ||
    recentOrgUIDs.length > MAX_RECENT_ORGANISATIONS ||
    recentOrgUIDs.some((orgId) => !isCanonicalOrgId(orgId))
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    cursor,
    search: searchValue.trim().toLocaleLowerCase("en"),
    recentOrgIds: Object.freeze([...new Set(recentOrgUIDs)]),
  });
}

export function createOrganisationLeaveOperationId() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID().replaceAll("-", "_");
  }
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    const bytes = globalThis.crypto.getRandomValues(new Uint8Array(24));
    return `leave_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("")}`;
  }
  fail("failed-precondition");
}

function validateContext(value) {
  if (
    !hasExactKeys(value, CONTEXT_KEYS) ||
    !isCanonicalOrgId(value.orgId) ||
    !isBoundedText(value.name, { minimum: 2, maximum: 80 }) ||
    (value.avatarAssetId !== null &&
      (typeof value.avatarAssetId !== "string" ||
        !ASSET_ID_PATTERN.test(value.avatarAssetId))) ||
    !hasExactKeys(value.currency, ["symbol", "name"]) ||
    !isBoundedText(value.currency.symbol, { minimum: 1, maximum: 16 }) ||
    Array.from(value.currency.symbol).length > 8 ||
    !isBoundedText(value.currency.name, { minimum: 1, maximum: 40 }) ||
    value.membershipStatus !== "active"
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    orgUID: value.orgId,
    // The bounded list intentionally omits public presentation identifiers.
    // Call getOwnOrganisationContext before using an organisation as a
    // private-authority target.
    publicIdentifier: null,
    name: value.name,
    // Canonical media assets are identifiers, not browser-trusted URLs. The
    // selector currently renders text only and must not turn this ID into src.
    logo: null,
    avatarAssetId: value.avatarAssetId,
    role: null,
    currency: value.currency.symbol,
    currencyName: value.currency.name,
    membershipStatus: value.membershipStatus,
  });
}

function validateOwnContextResponse(value, expectedOrgId) {
  if (
    !hasExactKeys(value, OWN_CONTEXT_KEYS) ||
    value.schemaVersion !== 1 ||
    value.orgId !== expectedOrgId ||
    value.membershipStatus !== "active" ||
    !Number.isSafeInteger(value.membershipRevision) ||
    value.membershipRevision < 1 ||
    !hasExactKeys(value.organisation, ORGANISATION_KEYS) ||
    !hasExactKeys(value.permissions, PERMISSION_KEYS) ||
    PERMISSION_KEYS.some((key) => typeof value.permissions[key] !== "boolean")
  ) {
    fail("invalid-response");
  }
  const organisation = value.organisation;
  if (
    typeof organisation.publicIdentifier !== "string" ||
    !PUBLIC_IDENTIFIER_PATTERN.test(organisation.publicIdentifier) ||
    !isBoundedText(organisation.name, { minimum: 2, maximum: 80 }) ||
    !isBoundedText(organisation.description, {
      maximum: 500,
      nullable: true,
    }) ||
    !isBoundedText(organisation.category, {
      maximum: 80,
      nullable: true,
    }) ||
    (organisation.avatarAssetId !== null &&
      (typeof organisation.avatarAssetId !== "string" ||
        !ASSET_ID_PATTERN.test(organisation.avatarAssetId))) ||
    !["private", "unlisted", "public"].includes(organisation.visibility) ||
    !hasExactKeys(organisation.economy, ["open"]) ||
    typeof organisation.economy.open !== "boolean" ||
    !hasExactKeys(organisation.currency, ["name", "symbol", "code"]) ||
    !isBoundedText(organisation.currency.name, {
      minimum: 1,
      maximum: 40,
    }) ||
    !isBoundedText(organisation.currency.symbol, {
      minimum: 1,
      maximum: 16,
    }) ||
    Array.from(organisation.currency.symbol).length > 8 ||
    typeof organisation.currency.code !== "string" ||
    !CURRENCY_CODE_PATTERN.test(organisation.currency.code) ||
    !["invite-only", "open"].includes(organisation.joinPolicy) ||
    !Number.isSafeInteger(organisation.revision) ||
    organisation.revision < 0
  ) {
    fail("invalid-response");
  }
  const context = validateContext({
    orgId: value.orgId,
    name: organisation.name,
    avatarAssetId: organisation.avatarAssetId,
    currency: {
      symbol: organisation.currency.symbol,
      name: organisation.currency.name,
    },
    membershipStatus: value.membershipStatus,
  });
  const permissions = Object.freeze(
    Object.fromEntries(
      PERMISSION_KEYS.map((key) => [key, value.permissions[key]]),
    ),
  );
  return Object.freeze({
    ...context,
    publicIdentifier: organisation.publicIdentifier,
    description: organisation.description,
    category: organisation.category,
    visibility: organisation.visibility,
    economyOpen: organisation.economy.open,
    currencyCode: organisation.currency.code,
    joinPolicy: organisation.joinPolicy,
    revision: organisation.revision,
    membershipRevision: value.membershipRevision,
    permissions,
  });
}

function validateListResponse(value, request) {
  if (
    !hasExactKeys(value, [
      "schemaVersion",
      "contexts",
      "recentContexts",
      "hasMore",
      "nextCursor",
    ]) ||
    value.schemaVersion !== 1 ||
    !Array.isArray(value.contexts) ||
    value.contexts.length > CONTEXT_PAGE_SIZE ||
    !Array.isArray(value.recentContexts) ||
    value.recentContexts.length > 5 ||
    typeof value.hasMore !== "boolean" ||
    (value.nextCursor !== null &&
      (typeof value.nextCursor !== "string" ||
        value.nextCursor.length < 64 ||
        value.nextCursor.length > 1024 ||
        !CURSOR_PATTERN.test(value.nextCursor))) ||
    value.hasMore !== (value.nextCursor !== null) ||
    (value.hasMore && value.contexts.length !== CONTEXT_PAGE_SIZE)
  ) {
    fail("invalid-response");
  }
  const contexts = value.contexts.map(validateContext);
  const recentContexts = value.recentContexts.map(validateContext);
  const requestedRecentPositions = new Map(
    request.recentOrgIds.map((orgId, index) => [orgId, index]),
  );
  const recentPositions = recentContexts.map(({ orgUID }) =>
    requestedRecentPositions.get(orgUID),
  );
  if (
    new Set(contexts.map(({ orgUID }) => orgUID)).size !== contexts.length ||
    new Set(recentContexts.map(({ orgUID }) => orgUID)).size !==
      recentContexts.length ||
    recentPositions.some((position) => position === undefined) ||
    recentPositions.some(
      (position, index) => index > 0 && position <= recentPositions[index - 1],
    ) ||
    (request.search.length > 0 &&
      contexts.some(
        ({ orgUID, name }) =>
          !orgUID.toLocaleLowerCase("en").includes(request.search) &&
          !name.toLocaleLowerCase("en").includes(request.search),
      )) ||
    (request.cursor !== null && value.nextCursor === request.cursor)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    contexts: Object.freeze(contexts),
    recentContexts: Object.freeze(recentContexts),
    hasMore: value.hasMore,
    nextCursor: value.nextCursor,
  });
}

function validateOrganisationAdministrationResponse(value, request) {
  if (
    !hasExactKeys(value, [
      "schemaVersion",
      "orgId",
      "organisationRevision",
      "capabilities",
      "roles",
      "members",
      "nextCursor",
      "hasMore",
    ]) ||
    value.schemaVersion !== 1 ||
    value.orgId !== request.orgId ||
    !Number.isSafeInteger(value.organisationRevision) ||
    value.organisationRevision < 1 ||
    !hasExactKeys(value.capabilities, ["canManageMembers", "canManageRoles"]) ||
    typeof value.capabilities.canManageMembers !== "boolean" ||
    typeof value.capabilities.canManageRoles !== "boolean" ||
    (!value.capabilities.canManageMembers && !value.capabilities.canManageRoles) ||
    !Array.isArray(value.roles) ||
    value.roles.length > 25 ||
    !Array.isArray(value.members) ||
    value.members.length > ADMINISTRATION_PAGE_SIZE ||
    typeof value.hasMore !== "boolean" ||
    (value.nextCursor !== null &&
      (typeof value.nextCursor !== "string" ||
        value.nextCursor.length < 64 ||
        value.nextCursor.length > 2048 ||
        !ADMINISTRATION_CURSOR_PATTERN.test(value.nextCursor))) ||
    value.hasMore !== (value.nextCursor !== null) ||
    (value.hasMore && value.members.length !== ADMINISTRATION_PAGE_SIZE) ||
    (request.cursor !== null && request.cursor === value.nextCursor)
  ) {
    fail("invalid-response");
  }

  const roles = value.roles.map((role) => {
    if (
      !hasExactKeys(role, [
        "roleId",
        "publicIdentifier",
        "title",
        "description",
        "definitionVisibility",
        "holderDisclosurePolicy",
        "disclosureEpoch",
        "maxHolders",
        "holderCount",
        "capabilities",
        "revision",
      ]) ||
      !isCanonicalOrgId(role.roleId) ||
      !PUBLIC_IDENTIFIER_PATTERN.test(role.publicIdentifier) ||
      !isBoundedText(role.title, { minimum: 1, maximum: 100 }) ||
      !isBoundedText(role.description, { maximum: 500, nullable: true }) ||
      !["public", "members", "administrators"].includes(
        role.definitionVisibility,
      ) ||
      !["transparent", "member-choice", "confidential"].includes(
        role.holderDisclosurePolicy,
      ) ||
      !Number.isSafeInteger(role.disclosureEpoch) ||
      role.disclosureEpoch < 0 ||
      !Number.isSafeInteger(role.maxHolders) ||
      role.maxHolders < 1 ||
      role.maxHolders > 25 ||
      !Number.isSafeInteger(role.holderCount) ||
      role.holderCount < 0 ||
      role.holderCount > role.maxHolders ||
      !hasExactKeys(role.capabilities, ROLE_CAPABILITY_KEYS) ||
      ROLE_CAPABILITY_KEYS.some(
        (key) => typeof role.capabilities[key] !== "boolean",
      ) ||
      !Number.isSafeInteger(role.revision) ||
      role.revision < 1
    ) {
      fail("invalid-response");
    }
    return Object.freeze({
      ...role,
      capabilities: Object.freeze({ ...role.capabilities }),
    });
  });
  const rolesById = new Map(roles.map((role) => [role.roleId, role]));
  if (rolesById.size !== roles.length) fail("invalid-response");

  const members = value.members.map((member) => {
    if (
      !hasExactKeys(member, [
        "membershipRef",
        "publicIdentifier",
        "displayName",
        "avatarAssetId",
        "revision",
        "isSelf",
        "assignments",
      ]) ||
      !OPAQUE_REFERENCE_PATTERN.test(member.membershipRef) ||
      !PUBLIC_IDENTIFIER_PATTERN.test(member.publicIdentifier) ||
      !isBoundedText(member.displayName, { minimum: 1, maximum: 80 }) ||
      (member.avatarAssetId !== null &&
        !ASSET_ID_PATTERN.test(member.avatarAssetId)) ||
      !Number.isSafeInteger(member.revision) ||
      member.revision < 1 ||
      typeof member.isSelf !== "boolean" ||
      !Array.isArray(member.assignments) ||
      member.assignments.length > 25
    ) {
      fail("invalid-response");
    }
    const assignments = member.assignments.map((assignment) => {
      if (
        !hasExactKeys(assignment, [
          "assignmentRef",
          "roleId",
          "status",
          "memberChoice",
          "acceptedDisclosureEpoch",
          "revision",
        ]) ||
        !OPAQUE_REFERENCE_PATTERN.test(assignment.assignmentRef) ||
        !rolesById.has(assignment.roleId) ||
        !["active", "pending-acceptance"].includes(assignment.status) ||
        !["administrators", "members", "public"].includes(
          assignment.memberChoice,
        ) ||
        (assignment.acceptedDisclosureEpoch !== null &&
          (!Number.isSafeInteger(assignment.acceptedDisclosureEpoch) ||
            assignment.acceptedDisclosureEpoch < 1)) ||
        !Number.isSafeInteger(assignment.revision) ||
        assignment.revision < 1
      ) {
        fail("invalid-response");
      }
      return Object.freeze({ ...assignment });
    });
    if (
      new Set(assignments.map(({ assignmentRef }) => assignmentRef)).size !==
      assignments.length
    ) {
      fail("invalid-response");
    }
    return Object.freeze({
      ...member,
      assignments: Object.freeze(assignments),
    });
  });
  if (
    new Set(members.map(({ membershipRef }) => membershipRef)).size !==
      members.length ||
    members.filter(({ isSelf }) => isSelf).length > 1
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    orgId: value.orgId,
    organisationRevision: value.organisationRevision,
    capabilities: Object.freeze({ ...value.capabilities }),
    roles: Object.freeze(roles),
    members: Object.freeze(members),
    nextCursor: value.nextCursor,
    hasMore: value.hasMore,
  });
}

export function createOrganisationContextService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  enabled = isOrgContextFeatureEnabled,
  permissionReadsEnabled = isOrgPermissionReadsFeatureEnabled,
  organisationCommandsEnabled =
    isOrganisationAuthorityCommandsFeatureEnabled,
} = {}) {
  let listCallable;
  let getCallable;
  let administrationCallable;
  let visibilityCallable;
  let leaveCallable;
  let privateLeaveCallable;
  return Object.freeze({
    async listOwnOrganisationContexts(options = {}) {
      if (!enabled) fail("feature-disabled");
      const request = normalizeListOptions(options);
      const actor = captureActor(authInstance);
      if (!listCallable) {
        listCallable = callableFactory(
          functionsInstance,
          "listOwnOrganisationContexts",
        );
      }
      try {
        const response = await withUiDeadline(
          listCallable({
            schemaVersion: 1,
            cursor: request.cursor,
            limit: CONTEXT_PAGE_SIZE,
            search: request.search,
            recentOrgIds: request.recentOrgIds,
          }),
          { phase: "organisation contexts" },
        );
        assertActorIsCurrent(authInstance, actor);
        return validateListResponse(response?.data, request);
      } catch (error) {
        assertActorIsCurrent(authInstance, actor);
        if (error instanceof OrganisationContextServiceError) throw error;
        const code = String(error?.code || "unknown").split("/").pop();
        throw new OrganisationContextServiceError(code);
      }
    },

    async getOwnOrganisationContext(orgUID) {
      if (!enabled || !permissionReadsEnabled) fail("feature-disabled");
      if (!isCanonicalOrgId(orgUID)) fail("invalid-argument");
      const actor = captureActor(authInstance);
      if (!getCallable) {
        getCallable = callableFactory(
          functionsInstance,
          "getOwnOrganisationContext",
        );
      }
      try {
        const response = await withUiDeadline(
          getCallable({ schemaVersion: 1, orgId: orgUID }),
          { phase: "acting organisation" },
        );
        assertActorIsCurrent(authInstance, actor);
        return validateOwnContextResponse(response?.data, orgUID);
      } catch (error) {
        assertActorIsCurrent(authInstance, actor);
        if (error instanceof OrganisationContextServiceError) throw error;
        const code = String(error?.code || "unknown").split("/").pop();
        throw new OrganisationContextServiceError(code);
      }
    },

    async getOrganisationAdministration({ orgId, cursor = null } = {}) {
      if (!enabled || !permissionReadsEnabled) fail("feature-disabled");
      if (
        !isCanonicalOrgId(orgId) ||
        (cursor !== null &&
          (typeof cursor !== "string" ||
            cursor.length < 64 ||
            cursor.length > 2048 ||
            !ADMINISTRATION_CURSOR_PATTERN.test(cursor)))
      ) {
        fail("invalid-argument");
      }
      const actor = captureActor(authInstance);
      if (!administrationCallable) {
        administrationCallable = callableFactory(
          functionsInstance,
          "getOrganisationAdministration",
        );
      }
      const request = Object.freeze({ orgId, cursor });
      try {
        const response = await withUiDeadline(
          administrationCallable({
            schemaVersion: 1,
            orgId,
            cursor,
            limit: ADMINISTRATION_PAGE_SIZE,
          }),
          { phase: "organisation administration" },
        );
        assertActorIsCurrent(authInstance, actor);
        return validateOrganisationAdministrationResponse(
          response?.data,
          request,
        );
      } catch (error) {
        assertActorIsCurrent(authInstance, actor);
        if (error instanceof OrganisationContextServiceError) throw error;
        const code = String(error?.code || "unknown").split("/").pop();
        throw new OrganisationContextServiceError(code);
      }
    },

    async setPublicOrganisationVisibility(orgUID, publicVisibility) {
      if (!enabled) fail("feature-disabled");
      if (!isLegacyOrgId(orgUID) || typeof publicVisibility !== "boolean") {
        fail("invalid-argument");
      }
      const actor = captureActor(authInstance);
      if (!visibilityCallable) {
        visibilityCallable = callableFactory(
          functionsInstance,
          "setPublicOrganisationVisibility",
        );
      }
      try {
        const response = await withUiDeadline(
          visibilityCallable({
            schemaVersion: 1,
            orgUID,
            public: publicVisibility,
          }),
          { phase: "organisation visibility" },
        );
        assertActorIsCurrent(authInstance, actor);
        const value = response?.data;
        if (
          !hasExactKeys(value, ["schemaVersion", "orgUID", "public"]) ||
          value.schemaVersion !== 1 ||
          value.orgUID !== orgUID ||
          value.public !== publicVisibility
        ) {
          fail("invalid-response");
        }
        return Object.freeze({ orgUID, public: publicVisibility });
      } catch (error) {
        assertActorIsCurrent(authInstance, actor);
        if (error instanceof OrganisationContextServiceError) throw error;
        const code = String(error?.code || "unknown").split("/").pop();
        throw new OrganisationContextServiceError(code);
      }
    },

    async leaveOwnOrganisation(
      orgUID,
      operationId = createOrganisationLeaveOperationId(),
    ) {
      if (!enabled) fail("feature-disabled");
      if (
        !isLegacyOrgId(orgUID) ||
        typeof operationId !== "string" ||
        !/^[A-Za-z0-9_-]{16,128}$/.test(operationId)
      ) {
        fail("invalid-argument");
      }
      const actor = captureActor(authInstance);
      if (!leaveCallable) {
        leaveCallable = callableFactory(functionsInstance, "leaveOwnOrganisation");
      }
      try {
        const response = await withUiDeadline(
          leaveCallable({ schemaVersion: 1, operationId, orgId: orgUID }),
          { phase: "leave organisation" },
        );
        assertActorIsCurrent(authInstance, actor);
        const value = response?.data;
        if (
          !hasExactKeys(value, ["active", "outcome", "replayed", "cleanup"]) ||
          value.active !== false ||
          value.outcome !== "left" ||
          typeof value.replayed !== "boolean" ||
          !["complete", "pending"].includes(value.cleanup)
        ) {
          fail("invalid-response");
        }
        return Object.freeze({
          active: false,
          outcome: "left",
          replayed: value.replayed,
          cleanup: value.cleanup,
        });
      } catch (error) {
        assertActorIsCurrent(authInstance, actor);
        if (error instanceof OrganisationContextServiceError) throw error;
        const code = String(error?.code || "unknown").split("/").pop();
        throw new OrganisationContextServiceError(code);
      }
    },

    async leavePrivateOrganisation(
      orgUID,
      expectedRevision,
      operationId = createOrganisationLeaveOperationId(),
    ) {
      if (!organisationCommandsEnabled) fail("feature-disabled");
      if (
        !isCanonicalOrgId(orgUID) ||
        !Number.isSafeInteger(expectedRevision) ||
        expectedRevision < 1 ||
        typeof operationId !== "string" ||
        !/^[A-Za-z0-9_-]{16,128}$/.test(operationId)
      ) {
        fail("invalid-argument");
      }
      const actor = captureActor(authInstance);
      if (!privateLeaveCallable) {
        privateLeaveCallable = callableFactory(
          functionsInstance,
          "leaveOrganisation",
        );
      }
      try {
        const response = await withUiDeadline(
          privateLeaveCallable({
            schemaVersion: 1,
            operationId,
            expectedRevision,
            orgId: orgUID,
          }),
          { phase: "leave organisation" },
        );
        assertActorIsCurrent(authInstance, actor);
        const value = response?.data;
        if (
          !hasExactKeys(value, [
            "schemaVersion",
            "orgId",
            "outcome",
            "membershipStatus",
            "membershipRevision",
          ]) ||
          value.schemaVersion !== 1 ||
          value.orgId !== orgUID ||
          value.outcome !== "left" ||
          value.membershipStatus !== "inactive" ||
          !Number.isSafeInteger(value.membershipRevision) ||
          value.membershipRevision <= expectedRevision
        ) {
          fail("invalid-response");
        }
        return Object.freeze({
          orgUID,
          outcome: "left",
          membershipStatus: "inactive",
          membershipRevision: value.membershipRevision,
        });
      } catch (error) {
        assertActorIsCurrent(authInstance, actor);
        if (error instanceof OrganisationContextServiceError) throw error;
        const code = String(error?.code || "unknown").split("/").pop();
        throw new OrganisationContextServiceError(code);
      }
    },
  });
}

export const organisationContextService = createOrganisationContextService();
